// Help.js — صفحة المساعدة الشاملة على هذا المسار.
//
// عارض للمحتوى في `helpContent.js` لا مالك له. وذلك الملفّ مولَّد آليًّا من مصدر واحد
// يتشاركه المساران (`frontend-Vit2/src/features/help/*.ts`) عبر
// `scripts/sync_help_content.js` — فالشرح لا يفترق بين واجهتين بصمت.
//
// الصفحة بلا RequirePermission عمدًا: هي شرح لا بيانات، وحجبها عن مستخدم يعني تركه أمام
// شاشة لا يفهمها. نفس معاملة «تغيير كلمة المرور».
import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  InputAdornment,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';
import { HELP_INTRO, HELP_SECTIONS } from './helpContent';

const pick = (value, lang) => (lang === 'ar' ? value.ar : value.en);

/** يجمع نصّ القسم كلّه ليعمل البحث على المحتوى لا على العناوين وحدها. */
function sectionText(section, lang) {
  const parts = [pick(section.title, lang), pick(section.summary, lang)];
  section.blocks.forEach((block) => {
    if (block.kind === 'p' || block.kind === 'sub' || block.kind === 'note') parts.push(pick(block.text, lang));
    if (block.kind === 'list') block.items.forEach((item) => parts.push(pick(item, lang)));
    if (block.kind === 'table') {
      block.head.forEach((cell) => parts.push(pick(cell, lang)));
      block.rows.forEach((row) => row.forEach((cell) => parts.push(pick(cell, lang))));
    }
    if (block.kind === 'flow') block.nodes.forEach((node) => parts.push(pick(node.label, lang)));
    if (block.kind === 'steps') {
      block.items.forEach((step) => {
        parts.push(pick(step.title, lang), pick(step.detail, lang));
        if (step.auto) parts.push(pick(step.auto, lang));
        if (step.status) parts.push(pick(step.status, lang));
      });
    }
  });
  return parts.join('   ').toLowerCase();
}

function BlockView({ block, lang }) {
  if (block.kind === 'p') {
    return <Typography variant="body2" sx={{ lineHeight: 2, color: 'text.primary' }}>{pick(block.text, lang)}</Typography>;
  }

  if (block.kind === 'sub') {
    return (
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, pb: 0.5, borderBottom: `1px solid ${COLORS.line}` }}>
        {pick(block.text, lang)}
      </Typography>
    );
  }

  if (block.kind === 'list') {
    return (
      <Stack spacing={1}>
        {block.items.map((item, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: COLORS.blue, mt: 1.2, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ lineHeight: 2 }}>{pick(item, lang)}</Typography>
          </Stack>
        ))}
      </Stack>
    );
  }

  if (block.kind === 'table') {
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {block.head.map((cell, index) => (
                <TableCell key={index} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{pick(cell, lang)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {block.rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <TableCell key={cellIndex} sx={{ verticalAlign: 'top', lineHeight: 1.9 }}>{pick(cell, lang)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (block.kind === 'flow') {
    // مربّعات متتابعة تنساب مع اتّجاه الصفحة: تُقرأ من اليمين في العربيّة ومن اليسار في
    // الإنجليزيّة بلا منطق اتّجاه خاصّ.
    return (
      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
        {block.nodes.map((node, index) => (
          <Stack key={index} direction="row" alignItems="center" gap={1}>
            <Paper variant="outlined" sx={{ px: 1.5, py: 1, minWidth: 130, textAlign: 'center', bgcolor: 'action.hover' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{pick(node.label, lang)}</Typography>
              {node.sub && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 11 }}>
                  {pick(node.sub, lang)}
                </Typography>
              )}
            </Paper>
            {index < block.nodes.length - 1 && (
              <Typography color="text.secondary" sx={{ transform: lang === 'ar' ? 'rotate(180deg)' : 'none' }}>&#8594;</Typography>
            )}
          </Stack>
        ))}
      </Stack>
    );
  }

  if (block.kind === 'steps') {
    return (
      <Stack spacing={1.5}>
        {block.items.map((step, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{pick(step.title, lang)}</Typography>
              {step.status && <Chip size="small" label={pick(step.status, lang)} />}
            </Stack>
            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 2 }}>{pick(step.detail, lang)}</Typography>
            {step.auto && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 1, px: 1, py: 0.5, borderRadius: 1, bgcolor: 'action.hover', lineHeight: 1.9 }}
                color="text.secondary"
              >
                {(lang === 'ar' ? 'يفعله النظام: ' : 'The system does: ') + pick(step.auto, lang)}
              </Typography>
            )}
          </Paper>
        ))}
      </Stack>
    );
  }

  if (block.kind === 'note') {
    const accent = block.tone === 'warn' ? COLORS.rose : COLORS.blue;
    return (
      <Box sx={{ borderInlineStart: `4px solid ${accent}`, bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 1 }}>
        <Typography variant="body2" sx={{ lineHeight: 2 }}>{pick(block.text, lang)}</Typography>
      </Box>
    );
  }

  return null;
}

export default function Help() {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const [query, setQuery] = useState('');

  const searchable = useMemo(
    () => HELP_SECTIONS.map((section) => ({ section, text: sectionText(section, lang) })),
    [lang]
  );
  const sections = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return HELP_SECTIONS;
    return searchable.filter((entry) => entry.text.includes(needle)).map((entry) => entry.section);
  }, [query, searchable]);

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ ...cardSx, p: 2, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {lang === 'ar' ? 'دليل استخدام النظام' : 'System user guide'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 2, maxWidth: 900 }}>
          {pick(HELP_INTRO, lang)}
        </Typography>
        <TextField
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={lang === 'ar' ? 'ابحث في الدليل...' : 'Search the guide...'}
          sx={{ mt: 2, maxWidth: 380, width: '100%' }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
        />
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '260px 1fr' }, gap: 2, alignItems: 'start' }}>
        <Paper sx={{ ...cardSx, p: 1.5, position: { lg: 'sticky' }, top: 16 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }} color="text.secondary">
            {lang === 'ar' ? 'المحتويات' : 'Contents'}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Stack spacing={0.5}>
            {sections.map((section) => (
              <MuiLink key={section.id} href={`#help-${section.id}`} underline="hover" variant="caption" sx={{ lineHeight: 2 }}>
                {pick(section.title, lang)}
              </MuiLink>
            ))}
          </Stack>
        </Paper>

        <Stack spacing={2}>
          {sections.length === 0 && (
            <Paper sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {lang === 'ar' ? 'لا نتيجة لهذا البحث.' : 'No section matches this search.'}
              </Typography>
            </Paper>
          )}
          {sections.map((section) => (
            <Paper key={section.id} id={`help-${section.id}`} sx={{ ...cardSx, p: 2, scrollMarginTop: 80 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{pick(section.title, lang)}</Typography>
              <Typography variant="caption" color="text.secondary">{pick(section.summary, lang)}</Typography>
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {section.blocks.map((block, index) => (
                  <BlockView key={index} block={block} lang={lang} />
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
